import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { BotContext } from '../../Bot.js';
import styles from './ChatBubble.module.css';
const ChatBubbleInternalContext = createContext({});
const ThumbsButton = ({ direction, onClick, disabled, isActive, }) => {
    const { useTailwindPrefix, isRatingConversation } = useContext(BotContext);
    return (_jsx("button", Object.assign({ type: "button", "aria-label": direction === 'up' ? 'Rate message positively' : 'Rate message negatively', onClick: onClick, disabled: disabled, className: classNames(styles.thumbsButton, Object.assign({}, (!useTailwindPrefix
            ? Object.assign({}, (disabled && !isRatingConversation
                ? {
                    'cursor-default': true,
                    'opacity-10': !isActive,
                }
                : {
                    'text-gray-400 hover:text-gray-600': !isActive,
                    'text-gray-800': isActive,
                    'cursor-progress': isRatingConversation,
                })) : Object.assign({}, (disabled && !isRatingConversation
            ? {
                'tw-cursor-default': true,
                'tw-opacity-10 hover:tw-text-gray-600': !isActive,
            }
            : {
                'tw-text-gray-400 hover:tw-text-gray-600': !isActive,
                'tw-text-gray-800': isActive,
                'tw-cursor-progress': isRatingConversation,
            }))))) }, { children: _jsx("svg", Object.assign({ viewBox: "0 0 32 32", id: "icon", xmlns: "http://www.w3.org/2000/svg", className: classNames({
                'w-4 h-4 fill-current': !useTailwindPrefix,
                'tw-w-4 tw-h-4 tw-fill-current': useTailwindPrefix,
            }) }, (direction === 'down' ? { style: { transform: 'rotate(180deg)' } } : {}), { children: _jsx("path", { d: "M26,12H20V6a3.0033,3.0033,0,0,0-3-3H14.8672a2.0094,2.0094,0,0,0-1.98,1.7173l-.8453,5.9165L8.4648,16H2V30H23a7.0078,7.0078,0,0,0,7-7V16A4.0045,4.0045,0,0,0,26,12ZM8,28H4V18H8Zm20-5a5.0057,5.0057,0,0,1-5,5H10V17.3027l3.9578-5.9365L14.8672,5H17a1.0008,1.0008,0,0,1,1,1v8h8a2.0025,2.0025,0,0,1,2,2Z", transform: "translate(0 0)" }) })) })));
};
const ChatBubbleContextComp = ({ role, messageKey, children, className, }) => {
    const { useTailwindPrefix } = useContext(BotContext);
    const isUser = role === 'USER';
    return (_jsx(ChatBubbleInternalContext.Provider, Object.assign({ value: { role, messageKey } }, { children: _jsx("div", Object.assign({ className: classNames(className, styles.bubbleContainer, Object.assign({}, (useTailwindPrefix
                ? {
                    'tw-flex tw-flex-col tw-gap-y-2': true,
                    'tw-items-end': isUser,
                    'tw-items-start': !isUser,
                }
                : {
                    'flex flex-col gap-y-2': true,
                    'items-end': isUser,
                    'items-start': !isUser,
                }))) }, { children: children })) })));
};
const ChatBubbleContent = ({ children, className }) => {
    const { role } = useContext(ChatBubbleInternalContext);
    const { useTailwindPrefix } = useContext(BotContext);
    const isUser = role === 'USER';
    return (_jsx("div", Object.assign({ className: classNames(className, styles.bubble, {
            [styles.isUser]: isUser,
            [styles.isAssistant]: !isUser,
        }) }, { children: _jsx("span", Object.assign({ className: classNames(Object.assign({ [styles.mainBubbleUser]: isUser }, (useTailwindPrefix
                ? {
                    'tw-inline-block tw-px-4 tw-py-3 tw-rounded-xl': true,
                    'tw-text-gray-800 tw-rounded-br-none': isUser,
                    'tw-bg-gray-200 tw-text-gray-800 tw-rounded-tl-none': !isUser,
                }
                : {
                    'inline-block px-4 py-3 rounded-xl': true,
                    'text-gray-800 rounded-br-none': isUser,
                    'bg-gray-200 text-gray-800 rounded-tl-none': !isUser,
                }))) }, { children: children })) })));
};
const ChatBubbleRate = ({ className }) => {
    const { role, messageKey } = useContext(ChatBubbleInternalContext);
    const { useTailwindPrefix, onRateConversation, isRatingConversation, ratings, ratingStatus } = useContext(BotContext);
    const intl = useIntl();
    const hasAttemptedRating = useRef(false);
    const exitTimerRef = useRef(null);
    const hideTimerRef = useRef(null);
    const [showToast, setShowToast] = useState(false);
    const [toastExiting, setToastExiting] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const ratedIsPositive = messageKey !== undefined ? ratings === null || ratings === void 0 ? void 0 : ratings[messageKey] : undefined;
    const isDisabled = isRatingConversation || ratedIsPositive !== undefined;
    const handleRate = (isPositive) => {
        hasAttemptedRating.current = true;
        setShowErrorMessage(false);
        onRateConversation === null || onRateConversation === void 0 ? void 0 : onRateConversation(isPositive, messageKey);
    };
    useEffect(() => {
        return () => {
            if (exitTimerRef.current)
                clearTimeout(exitTimerRef.current);
            if (hideTimerRef.current)
                clearTimeout(hideTimerRef.current);
        };
    }, []);
    useEffect(() => {
        if (!hasAttemptedRating.current)
            return;
        if (ratingStatus === 'success' || ratingStatus === 'error') {
            hasAttemptedRating.current = false;
            if (ratingStatus === 'success') {
                setShowErrorMessage(false);
                setShowToast(true);
                setToastExiting(false);
            }
            else {
                setShowToast(false);
                setShowErrorMessage(true);
                setToastExiting(false);
            }
            if (exitTimerRef.current)
                clearTimeout(exitTimerRef.current);
            if (hideTimerRef.current)
                clearTimeout(hideTimerRef.current);
            exitTimerRef.current = setTimeout(() => setToastExiting(true), 2300);
            hideTimerRef.current = setTimeout(() => {
                setShowToast(false);
                setShowErrorMessage(false);
                setToastExiting(false);
            }, 2600);
        }
    }, [ratingStatus]);
    if (role === 'USER' || !onRateConversation)
        return null;
    return (_jsx("div", Object.assign({ className: classNames(className, {
            'flex justify-start': !useTailwindPrefix,
            'tw-flex tw-justify-start': useTailwindPrefix,
        }) }, { children: _jsxs("div", Object.assign({ className: classNames({
                'flex gap-2': !useTailwindPrefix,
                'tw-flex tw-gap-2': useTailwindPrefix,
            }) }, { children: [_jsx(ThumbsButton, { direction: "up", onClick: () => handleRate(true), disabled: isDisabled, isActive: ratedIsPositive === true }), _jsx(ThumbsButton, { direction: "down", onClick: () => handleRate(false), disabled: isDisabled, isActive: ratedIsPositive === false }), showToast && (_jsx("span", Object.assign({ className: classNames(styles.ratingToast, { [styles.ratingToastExiting]: toastExiting }) }, { children: intl.formatMessage({ id: 'maia_chatbot_skill_rating_thanks' }) }))), showErrorMessage && !showToast && (_jsx("span", Object.assign({ className: classNames(styles.ratingError, { [styles.ratingErrorExiting]: toastExiting }) }, { children: intl.formatMessage({ id: 'maia_chatbot_skill_rating_error' }) })))] })) })));
};
const ChatBubbleRoot = ({ children, role, className, showRateSelector, messageKey, }) => (_jsxs(ChatBubbleContextComp, Object.assign({ role: role, messageKey: messageKey }, { children: [_jsx(ChatBubbleContent, Object.assign({ className: className }, { children: children })), showRateSelector && _jsx(ChatBubbleRate, {})] })));
export const ChatBubble = Object.assign(ChatBubbleRoot, {
    Context: ChatBubbleContextComp,
    Content: ChatBubbleContent,
    Rate: ChatBubbleRate,
});
